import React from 'react';
import { Card, CardContent } from './ui/card';
import { MessageCircle, UserCheck, CheckCircle2, CreditCard } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    {
      icon: MessageCircle,
      title: 'Choose an offer',
      description: 'Browse our premium subscriptions and select the one you want',
      color: 'text-primary',
      bgColor: 'bg-primary/20'
    },
    {
      icon: UserCheck,
      title: 'Message us on Instagram',
      description: 'Contact us with your chosen offer and we\'ll guide you through',
      color: 'text-accent',
      bgColor: 'bg-accent/20'
    },
    {
      icon: CheckCircle2,
      title: 'We activate with you',
      description: 'We work together to activate and confirm your subscription works',
      color: 'text-secondary',
      bgColor: 'bg-secondary/20'
    },
    {
      icon: CreditCard,
      title: 'You pay after activation',
      description: 'Only pay once everything is confirmed and working perfectly',
      color: 'text-success',
      bgColor: 'bg-success/20'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 px-4">
      <div className="container mx-auto">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
            How It <span className="gradient-text">Works</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Simple, transparent, and secure. Here's our proven 4-step process.
          </p>
        </div>

        {/* Steps Timeline */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={index} className="glass-card hover-glow group relative overflow-hidden">
                  {/* Step Number */}
                  <div className="absolute -top-4 -right-4 text-8xl font-bold text-muted/5 group-hover:text-muted/10 transition-colors">
                    {index + 1}
                  </div>
                  
                  <CardContent className="p-6 space-y-4 relative z-10">
                    <div className={`w-14 h-14 rounded-full ${step.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${step.color}`} />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-foreground">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </CardContent>

                  {/* Connecting Line (hidden on mobile and last item) */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-border to-transparent" />
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Trust Paragraph */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="glass-card border-accent/30">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Transparency & Support
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                At Meta Learners, we prioritize your peace of mind. We never ask for passwords, 
                we guide you through every step, and we only get paid after you confirm everything works. 
                Our customer-first approach means clear communication, full support from start to finish, 
                and zero risk on your part. We're here to help, not to rush you.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
